/**
 * Cart Synchronization Manager
 * Handles cart syncing for logged-in customers and cleanup for logged-out users
 */
class CartSyncManager {
  constructor(customerId = null, token = null, currentDate = null) {
    this.customerId = customerId;
    this.token = token;
    this.currentDate = currentDate;
    this.cartCookieName = customerId ? `cart_${customerId}` : null;
  }

  /**
   * Initialize cart synchronization based on customer login status
   */
  init() {
    if (this.customerId) {
      this.handleLoggedInUser();
    } else {
      this.handleLoggedOutUser();
    }
  }

  /**
   * Handle cart sync for logged-in customers
   */
  handleLoggedInUser() {
    // Clear any other customer's cart cookies first
    this.clearOtherCustomerCookies();
    
    // Only run sync logic if not already reloaded after login
    const syncKey = `cartSynced_${this.customerId}`;
    if (!sessionStorage.getItem(syncKey)) {
      sessionStorage.setItem(syncKey, "true");
      this.syncCustomerCart();
    }
  }

  /**
   * Handle cleanup for logged-out users
   */
  handleLoggedOutUser() {
    // Clear ALL customer cart cookies when logged out
    document.cookie.split(";").forEach(cookie => {
      const cookieName = cookie.trim().split("=")[0];
      if (cookieName.startsWith("cart_") || cookieName === "cart") {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    });
    
    // Clear session storage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith("cartSynced_") || key === "reloadedAfterLogin") {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear cookies from other customers
   */
  clearOtherCustomerCookies() {
    document.cookie.split(";").forEach(cookie => {
      const cookieName = cookie.trim().split("=")[0];
      if (cookieName.startsWith("cart_") && cookieName !== this.cartCookieName) {
        document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    });
  }

  /**
   * Synchronize customer cart based on token age and availability
   */
  syncCustomerCart() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (this.token && this.currentDate) {
      const currentDateObj = new Date(this.currentDate);
      const daysDifference = (today - currentDateObj) / (1000 * 60 * 60 * 24);
      
      if (daysDifference < 10) {
        // Token is recent, use it
        this.setCartCookies(this.token);
        window.location.reload();
      } else {
        // Token is old, update with current cart
        const currentCartToken = this.getCookie("cart") || "";
        this.updateMetafield(currentCartToken).then(() => {
          window.location.reload();
        });
      }
    } else {
      // No saved token, save current cart if exists
      const currentCart = this.getCookie("cart");
      if (currentCart) {
        this.updateMetafield(currentCart).then(() => {
          document.cookie = `${this.cartCookieName}=${currentCart}; path=/`;
          window.location.reload();
        });
      } else {
        // No cart data, just reload for consistency
        window.location.reload();
      }
    }
  }

  /**
   * Set cart cookies for both customer-specific and Shopify's cart system
   */
  setCartCookies(token) {
    document.cookie = `${this.cartCookieName}=${token}; path=/`;
    document.cookie = `cart=${token}; path=/`; // For Shopify's cart system
  }

  /**
   * Update customer metafield with cart token
   */
  updateMetafield(cartToken = "") {
    return fetch('/apps/rcapp/customerCartToken', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: `gid://shopify/Customer/${this.customerId}`,
        token: cartToken,
        date: new Date().toISOString()
      }),
    })
    .then(res => res.json())
    .catch(err => {
      console.error("❌ Failed to sync cart token:", err);
      throw err;
    });
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const cookieString = `; ${document.cookie}`;
    const cookies = cookieString.split(`; ${name}=`);
    return cookies.length === 2 ? cookies.pop().split(';').shift() : null;
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartSyncManager;
} else {
  window.CartSyncManager = CartSyncManager;
}